import React, { useEffect } from 'react';

interface LinkedInWindowProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
  onMinimize: () => void;
  zIndex: number;
  onClick: () => void;
}

/**
 * LinkedIn profile window with an iframe embed attempt and a static fallback profile card.
 * @param {LinkedInWindowProps} props - Standard window props (open, maximize, minimize, close, zIndex, onClick).
 */
const LinkedInWindow: React.FC<LinkedInWindowProps> = ({ isOpen, isMaximized, onClose, onMaximize, onMinimize, zIndex, onClick }) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={`window-overlay linkedin ${isMaximized ? 'maximized' : ''}`} style={{ zIndex }}>
      <div className={`window linkedin-window ${isMaximized ? 'maximized' : ''}`} onClick={onClick}>
        <div className="window-titlebar">
          <div className="window-title">
            <img 
              src="https://www.linkedin.com/favicon.ico" 
              alt="LinkedIn"
              width="16"
              height="16"
              className="window-icon"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  const fallback = document.createElement('span');
                  fallback.textContent = '🔗';
                  fallback.className = 'window-icon';
                  parent.insertBefore(fallback, parent.firstChild);
                }
              }}
            />
            LinkedIn - Jake Zhao
          </div>
          <div className="window-controls">
            <button className="window-control minimize" onClick={onMinimize}>−</button>
            <button className="window-control maximize" onClick={onMaximize}>
              {isMaximized ? '🗗' : '⬜'}
            </button>
            <button className="window-control close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="window-content">
          {/* Try multiple embedding approaches */}
          <iframe 
            src="https://linkedin.com/in/jakemzhao"
            width="100%"
            height="100%"
            frameBorder="0"
            allowFullScreen
            title="Jake Zhao LinkedIn Profile"
            sandbox="allow-scripts allow-same-origin allow-top-navigation"
            onError={() => console.log('LinkedIn iframe blocked')}
          />
          
          {/* Alternative: LinkedIn badge widget */}
          <div className="linkedin-badge-fallback" style={{display: 'none'}}>
            <div 
              className="badge-base LI-profile-badge" 
              data-locale="en_US" 
              data-size="large" 
              data-theme="light" 
              data-type="HORIZONTAL" 
              data-vanity="jakemzhao" 
              data-version="v1"
            >
              <a 
                className="badge-base__link LI-simple-link" 
                href="https://www.linkedin.com/in/jakemzhao?trk=profile-badge"
              >
                Jake Zhao
              </a>
            </div>
          </div>
          
          {/* Main fallback with enhanced profile preview */}
          <div className="linkedin-fallback">
            <div className="linkedin-profile">
              <div className="profile-header">
                <div className="profile-avatar">JZ</div>
                <div className="profile-info">
                  <h2>Jake Zhao</h2>
                  <p className="profile-title">Data Engineering Manager</p>
                  <p className="profile-location">📍 San Diego, CA</p>
                </div>
              </div>
              
              <div className="profile-stats">
                <div className="stat">
                  <strong>500+</strong>
                  <span>connections</span>
                </div>
              </div>
              
              <div className="profile-actions">
                <a 
                  href="https://linkedin.com/in/jakemzhao" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="linkedin-link primary"
                >
                  View Full Profile
                </a>
                <a 
                  href="https://linkedin.com/in/jakemzhao" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="linkedin-link secondary"
                >
                  Connect
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInWindow;